import {useState} from "react";
import {useSelector} from "react-redux";
import DeliverableComment from "./deliverable-comment";
import {
    normalizeMembershipEntity,
    prepareMembershipPayload,
} from "../workspace-membership/workspace-membership-identity";

const commentPopulate = {
    children: {
        populate: {
            user: {populate: {member: {populate: "profileImage"}}},
        },
    },
    user: {populate: {member: {populate: "profileImage"}}},
    attachments: true,
};

export function useDeliverableComment() {
    const [loading, setLoading] = useState(false);
    const workspaceMembership = useSelector((state) => state.app?.workspaceMembershipData);
    const memberships = workspaceMembership?.workspace?.workspace_memberships || [];

    const normalizeComment = (item) => {
        const normalized = normalizeMembershipEntity(item, "deliverableComment", memberships);
        if (!normalized) return normalized;
        normalized.children = (normalized.children || []).map((child) =>
            normalizeMembershipEntity(child, "deliverableComment", memberships));
        return normalized;
    };

    const getDeliverableComments = async (deliverableId) => {
        if (!deliverableId) return [];

        setLoading(true);
        try {
            const deliverableComment = new DeliverableComment();
            const query = {
                filters: {
                    $and: [
                        {deliverable: {$eq: deliverableId}},
                    ],
                },
                populate: commentPopulate,
            };
            const _data = await deliverableComment.getAll(query);

            return (_data.data || []).map(normalizeComment);
        } catch (e) {
            throw e;
        } finally {
            setLoading(false);
        }
    };

    const updateDeliverableComment = async (commentId, commentData) => {
        if (!commentId) return null;

        setLoading(true);
        try {
            const deliverableComment = new DeliverableComment();
            const payload = prepareMembershipPayload("deliverableComment", commentData, memberships, {
                currentMembership: workspaceMembership,
                operation: "update",
            });
            const _data = await deliverableComment.update(commentId, payload, {populate: commentPopulate});
            return normalizeComment(_data.data);
        } catch (e) {
            throw e;
        } finally {
            setLoading(false);
        }
    };

    const createDeliverableComment = async (commentData) => {
        setLoading(true);
        try {
            const deliverableComment = new DeliverableComment();
            const query = {populate: commentPopulate};
            const payload = prepareMembershipPayload("deliverableComment", commentData, memberships, {
                currentMembership: workspaceMembership,
                operation: "create",
            });
            const _data = await deliverableComment.create(payload, query);
            return normalizeComment(_data.data);
        } catch (e) {
            throw e;
        } finally {
            setLoading(false);
        }
    };

    const deleteDeliverableComment = async (commentId) => {
        if (!commentId) return null;
        setLoading(true);
        try {
            const deliverableComment = new DeliverableComment();
            return await deliverableComment.delete(commentId);
        } finally {
            setLoading(false);
        }
    };

    return {
        loading,
        getDeliverableComments,
        updateDeliverableComment,
        createDeliverableComment,
        deleteDeliverableComment,
    };
}
